import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getAuth } from '../utils/auth';
import { usePermissions, PermissionGate } from '../contexts/PermissionContext';
import { adminAPI } from '../services/api';
import {
  LuLayoutDashboard, LuPackage, LuArrowDownToLine, LuArrowUpFromLine,
  LuClipboardList, LuFileText, LuUsers, LuUserCog, LuShield,
  LuSettings, LuWarehouse, LuX, LuRotateCcw, LuTriangleAlert, LuBoxes
} from 'react-icons/lu';

const Sidebar = ({ onNavigate }) => {
  const location = useLocation();
  const { userData: user } = getAuth();
  const { loading } = usePermissions();
  const adminManagerRoles = ['system_admin', 'admin', 'manager'];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const canViewZohoWebhooks = user?.user_type === 'internal' && userRoles.some((role) => adminManagerRoles.includes(role));
  
  // Fetch system branding for company name and logo (accessible to all authenticated users)
  const { data: systemBranding, isLoading: brandingLoading, error: brandingError } = useQuery(
    'systemBranding',
    () => adminAPI.getSystemBranding(),
    {
      staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes (reduced from 5)
      cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes (reduced from 10)
      retry: 2, // Retry twice on failure
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      enabled: true // Always try to fetch - this endpoint is accessible to all authenticated users
    }
  );
  const { data: appVersion } = useQuery(
    'appVersion',
    () => adminAPI.getAppVersion(),
    {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  );
  // Get company name and logo from branding or use fallback
  const companyName = systemBranding?.company_name || '3PL System';
  const version = appVersion?.version || process.env.REACT_APP_APP_VERSION || 'dev';
  const logoUrl = systemBranding?.has_logo_file 
    ? `/api/system-settings/logo?t=${systemBranding.logo_updated_at ? new Date(systemBranding.logo_updated_at).getTime() : Date.now()}` 
    : systemBranding?.logo_url;
  
  // Function to check if a link is active
  const isActive = (path) => {
    // Handle paths with query parameters for the new workflow structure
    if (path.includes('?')) {
      const [basePath, queryString] = path.split('?');
      if (location.pathname === basePath) {
        const pathParams = new URLSearchParams(queryString);
        const currentParams = new URLSearchParams(location.search);
        
        // Check if the type parameter matches
        const pathType = pathParams.get('type');
        const currentType = currentParams.get('type');
        
        if (pathType && currentType) {
          // Handle comma-separated types (e.g., "pick_pack,kit_build")
          const pathTypes = pathType.split(',').sort();
          const currentTypes = currentType.split(',').sort();
          return JSON.stringify(pathTypes) === JSON.stringify(currentTypes);
        }
        
        return pathType === currentType;
      }
      return false;
    }
    
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Render the company branding header
  const renderCompanyBranding = () => {
    return (
      <div className="p-4 border-b border-gray-700 relative">
        {/* Mobile close button */}
        {onNavigate && (
          <button
            type="button"
            className="absolute top-3 right-3 md:hidden text-gray-400 hover:text-white focus:outline-none"
            onClick={onNavigate}
          >
            <span className="sr-only">Close sidebar</span>
            <LuX className="h-6 w-6" />
          </button>
        )}
        <div className="flex flex-col items-center space-y-2">
          {/* Logo */}
          {logoUrl ? (
            <div className="w-full flex justify-center items-center">
              <img
                src={logoUrl}
                alt={`${companyName} Logo`}
                className="max-w-[200px] max-h-[60px] lg:max-h-[60px] sm:max-h-[50px] object-contain mx-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          ) : null}
          
          {/* Company Name */}
          <div 
            className="text-center text-white font-bold text-lg lg:text-lg sm:text-base leading-tight"
            title={companyName}
          >
            <div className="truncate max-w-full">
              {companyName}
            </div>
          </div>
          
          {/* User Role/Info */}
          <div className="text-center text-gray-300 text-sm lg:text-sm sm:text-xs">
            {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Admin'}
          </div>
        </div>
      </div>
    );
  };

  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <aside className="w-64 bg-gray-800 text-white h-full overflow-y-auto">
        {renderCompanyBranding()}
        <div className="flex justify-center items-center p-4">
          <div className="text-gray-400">Loading...</div>
        </div>
      </aside>
    );
  }
  
  return (
    <aside className="flex h-full w-64 flex-col bg-gray-800 text-white">
      {renderCompanyBranding()}
      <nav className="mt-5 flex-1 overflow-y-auto px-2 pb-4" onClick={(e) => {
        // Close sidebar on mobile when a nav link is clicked
        if (onNavigate && e.target.closest('a')) onNavigate();
      }}>
        {/* Dashboard - Standalone */}
        <PermissionGate permissions="dashboard">
          <Link 
            to="/admin" 
            className={`${
              isActive('/admin') && location.pathname === '/admin' 
                ? 'bg-gray-900 text-white' 
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-6`}
          >
            <LuLayoutDashboard className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
            Dashboard
          </Link>
        </PermissionGate>

        {/* Warehouse Operations Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Warehouse Operations
          </h3>
          
          {/* Inventory */}
          <PermissionGate permissions="view_inventory">
            <Link 
              to="/admin/inventory" 
              className={`${
                isActive('/admin/inventory') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuPackage className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Inventory
            </Link>
          </PermissionGate>
          
          {/* Inbound */}
          <PermissionGate permissions="incoming_inventory">
            <Link 
              to="/admin/requests?type=incoming,crossdock,inbound_manifest" 
              className={`${
                isActive('/admin/requests?type=incoming') || isActive('/admin/requests?type=incoming,crossdock') || isActive('/admin/requests?type=incoming,crossdock,inbound_manifest')
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuArrowDownToLine className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Inbound
            </Link>
          </PermissionGate>

          
          {/* Outbound */}
          <PermissionGate permissions="ship_products">
            <Link 
              to="/admin/requests?type=ship" 
              className={`${
                isActive('/admin/requests?type=ship')
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuArrowUpFromLine className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Outbound
            </Link>
          </PermissionGate>
          
          {/* All Requests */}
          <PermissionGate permissions={['pick_pack', 'kit_building', 'incoming_inventory', 'ship_products']}>
            <Link 
              to="/admin/requests" 
              className={`${
                isActive('/admin/requests') && !location.search
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuClipboardList className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              All Requests
            </Link>
          </PermissionGate>
        </div>

        {/* Billing Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Billing
          </h3>
          
          {/* Invoices */}
          <PermissionGate permissions="view_invoices">
            <Link 
              to="/admin/invoices" 
              className={`${
                isActive('/admin/invoices') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuFileText className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Invoices
            </Link>
          </PermissionGate>
        </div>

        {/* Operations Section */}
        <div className="mb-6">
          <h3 className="px-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Operations
          </h3>
          
          {/* Customers */}
          <PermissionGate permissions="view_customers">
            <Link 
              to="/admin/customers" 
              className={`${
                isActive('/admin/customers') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuUsers className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Customers
            </Link>
          </PermissionGate>
          
          {/* Users */}
          <PermissionGate permissions={['view_users', 'create_users', 'edit_users']}>
            <Link 
              to="/admin/users" 
              className={`${
                isActive('/admin/users') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuUserCog className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Users
            </Link>
          </PermissionGate>
          
          {/* Audit Logs */}
          <PermissionGate permissions="audit_logs.read">
            <Link
              to="/admin/audit-logs"
              className={`${
                isActive('/admin/audit-logs')
                  ? 'bg-gray-900 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuShield className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Audit Logs
            </Link>
          </PermissionGate>

          <Link
            to="/admin/storage-units"
            className={`${
              isActive('/admin/storage-units')
                ? 'bg-gray-900 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <LuBoxes className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
            Storage Units
          </Link>

          <Link
            to="/admin/corrections"
            className={`${
              isActive('/admin/corrections')
                ? 'bg-gray-900 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <LuRotateCcw className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
            Corrections
          </Link>

          <Link
            to="/admin/billing-exceptions"
            className={`${
              isActive('/admin/billing-exceptions')
                ? 'bg-gray-900 text-white'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
          >
            <LuTriangleAlert className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
            Billing Exceptions
          </Link>

          {canViewZohoWebhooks && (
            <Link 
              to="/admin/zoho-webhooks" 
              className={`${
                isActive('/admin/zoho-webhooks') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuWarehouse className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Zoho Webhooks
            </Link>
          )}

          {/* Settings */}
          <PermissionGate permissions={['view_general_settings', 'edit_general_settings', 'view_notifications', 'edit_notifications', 'view_security_settings', 'edit_security_settings', 'view_warehouses', 'manage_roles', 'manage_teams']}>
            <Link 
              to="/admin/settings" 
              className={`${
                isActive('/admin/settings') 
                  ? 'bg-gray-900 text-white' 
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md mb-1`}
            >
              <LuSettings className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-300" />
              Settings
            </Link>
          </PermissionGate>
        </div>
      </nav>
      <div className="border-t border-gray-700 px-4 py-3">
        <Link
          to="/admin/change-log"
          className="text-xs font-medium text-gray-400 transition-colors hover:text-white"
          onClick={onNavigate}
        >
          Version {version}
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar; 
